import { useState } from 'react';
import {
  X,
  FolderKanban,
  Target,
  Brain,
  BarChart3,
  ExternalLink,
  Users,
  Calendar,
  FileText,
  Layers,
  Clock,
  CheckCircle,
  Rocket,
  Zap,
  MessageSquare,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import { useLexLastSync } from '../contexts/LexLastSyncContext';

interface GestionProjetsPRIsMEModalProps {
  isOpen: boolean;
  onClose: () => void;
  department: string;
  domain: string;
}

const tabs = [
  { id: 'overview', label: "Vue d'ensemble", icon: Target },
  { id: 'raci', label: 'RACI & Gouvernance', icon: Users },
  { id: 'contenu', label: 'Types, vues & Rapports', icon: FileText },
  { id: 'ia', label: 'Analyse IA & Cédules', icon: Brain },
  { id: 'processus', label: 'Processus Complet', icon: BarChart3 },
];

export default function GestionProjetsPRIsMEModal({
  isOpen,
  onClose,
  department,
  domain: _domain,
}: GestionProjetsPRIsMEModalProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = isOpen ? (lexDates['gestion_projets_prisme_last_ai_analysis'] ?? null) : null;
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleGoToProjets = () => {
    navigate('/projets');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] my-4 max-h-[95vh] flex flex-col overflow-hidden"
          >
            <div className="bg-linear-to-r from-bz-dark via-bz-blue to-indigo-700 text-white p-8 relative">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-white" />
              </button>
              <div className="flex items-start gap-4 pr-12">
                <div className="w-16 h-16 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <FolderKanban className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="text-sm font-semibold text-white/80">Outil Projets PRIsME</div>
                    <div className="flex items-center gap-1 px-2 py-0.5 bg-white/20 backdrop-blur-sm rounded-full">
                      <Brain className="w-3 h-3 text-yellow-300" />
                      <span className="text-xs font-semibold text-white">IA</span>
                    </div>
                  </div>
                  <h2 className="text-3xl font-bold mb-2">Gestion de projets PRIsME</h2>
                  <p className="text-white/90 text-lg mb-2">{department}</p>
                  {lastAnalysisDate && (
                    <div className="flex items-center gap-2 mt-2 text-xs text-white/70">
                      <Clock className="w-3.5 h-3.5" />
                      <span>Dernière analyse AI pour LEX: {formatLastAnalysisDate(lastAnalysisDate)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="border-b border-gray-200 bg-gray-50">
              <div className="flex overflow-x-auto">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap
                        ${activeTab === tab.id
                          ? 'text-bz-blue border-b-2 border-bz-blue bg-white'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }
                      `}
                    >
                      <Icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {activeTab === 'overview' && <OverviewTab />}
                  {activeTab === 'raci' && <RaciTab />}
                  {activeTab === 'contenu' && <ContenuTab />}
                  {activeTab === 'ia' && <IATab />}
                  {activeTab === 'processus' && <ProcessusTab />}
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="bg-linear-to-r from-gray-50 to-blue-50 px-8 py-6 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Prêt à gérer vos projets selon les normes PMBOK ?
              </div>
              <button
                onClick={handleGoToProjets}
                className="flex items-center gap-2 px-6 py-3 bg-bz-blue text-white rounded-lg font-semibold hover:bg-bz-dark transition-colors shadow-lg hover:shadow-xl"
              >
                <span>Accéder à l&apos;outil Projets</span>
                <ExternalLink className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

function OverviewTab() {
  return (
    <div className="space-y-6">
      <div className="bg-linear-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border-l-4 border-bz-blue">
        <h3 className="text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
          <Rocket className="w-6 h-6 text-bz-blue" />
          Règle d&apos;or : tout projet BZ démarre dans l&apos;outil Projets PRIsME
        </h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          Pour qu&apos;un projet démarre chez BZ, il doit être <strong>ajouté dans l&apos;outil de projet PRIsME</strong>.
          Le <strong>contenu du projet</strong> (objectifs, périmètre, jalons) est défini par le <strong>chargé de projet technique</strong>.
          La gouvernance overall est assurée par les chargés de projet overall : <strong>Eric Hains pour les TI</strong> et <strong>Francis Marquis pour la Cybersécurité</strong>.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-bz-blue">
            <div className="text-lg font-bold text-bz-blue mb-1">A (Accountable)</div>
            <div className="text-sm text-gray-600">Eric Hains (TI) · Francis Marquis (Cyber)</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-emerald-600">
            <div className="text-lg font-bold text-emerald-600 mb-1">R (Responsible)</div>
            <div className="text-sm text-gray-600">Primes TI · Spécialistes Cybersécurité</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-purple-600">
            <div className="text-lg font-bold text-purple-600 mb-1">PMBOK</div>
            <div className="text-sm text-gray-600">Normes projet · Vues GANTT · KanBan</div>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-bz-blue" />
          Objectif
        </h4>
        <p className="text-gray-700 leading-relaxed">
          Centraliser tous les projets BZ dans un seul outil, avec <strong>RACI à trois niveaux</strong> (Projet, Jalon, Tâche),
          <strong> commentaires</strong> à ces trois niveaux, <strong>analyse IA</strong> globale et par projet, et <strong>cédules personnalisées par projet</strong> pour les rapports automatiques.
        </p>
      </div>

      {/* La page Projets en bref — tiré de l'analyse de la page réelle */}
      <div className="bg-slate-50 border border-slate-200 p-6 rounded-xl">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-slate-600" />
          La page Projets en bref
        </h4>
        <p className="text-gray-700 text-sm mb-4">
          Sur la page <strong>Projets</strong>, tu as un bandeau avec les onglets <strong>Projets</strong> et <strong>Tâches</strong>, puis les vues <strong>Cartes</strong>, <strong>Liste</strong> et <strong>Gantt</strong>. Une barre de filtres permet de filtrer par statut (Terminé, En cours, En pause, Planifié), par <strong>Client</strong>, par <strong>Assigné</strong> (parties prenantes en mode PRIsME), par recherche textuelle, et d’ouvrir l’<strong>Analyse AI</strong> globale. Les filtres avancés ajoutent le <strong>niveau</strong> (Stratégique, Tactique, Opérationnel) et une date « Avant le ». Les admins peuvent basculer entre <strong>Vue restreinte</strong> (projets où tu as des droits) et <strong>Vue complète</strong>.
        </p>
        <p className="text-gray-700 text-sm mb-2">
          Chaque <strong>carte projet</strong> affiche : image, priorité, catégorie (ex. Programmation, Cybersécurité, Service TI), type Stratégique/Tactique/Opérationnel, client, <strong>équipe RACI</strong> (avatars), <strong>progression calculée à partir des tâches</strong>, et des compteurs pour les tâches, jalons et commentaires. Les boutons d’action donnent accès aux <strong>Tâches</strong>, à la <strong>Vue Jalons</strong>, à l’édition, aux <strong>Rapports</strong> (PDF, Excel, KPI, Bonus), à l’<strong>Automation</strong> (cédules), aux Commentaires, au KPI et à l’<strong>Analyse AI</strong> du projet.
        </p>
      </div>
    </div>
  );
}

function RaciTab() {
  return (
    <div className="space-y-6">
      <div className="bg-linear-to-r from-purple-50 to-indigo-50 p-6 rounded-xl">
        <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Users className="w-6 h-6 text-purple-600" />
          RACI au niveau Projet, Jalon et Tâche
        </h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          L&apos;outil Projets PRIsME applique la matrice <strong>RACI</strong> (Responsible, Accountable, Consulted, Informed)
          à <strong>trois niveaux</strong> : au niveau du <strong>projet</strong> (attributions plan), au niveau des <strong>jalons</strong> (phases),
          et au niveau des <strong>tâches</strong>. Les <strong>commentaires</strong> sont également disponibles aux trois niveaux pour la traçabilité et la collaboration.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-lg border-2 border-purple-200">
            <h5 className="font-bold text-purple-900 mb-2">Niveau Projet</h5>
            <p className="text-sm text-gray-700">Attributions RACI sur le plan (lk_projet_contact, id_tache NULL). Chargé de projet overall = A.</p>
          </div>
          <div className="bg-white p-4 rounded-lg border-2 border-blue-200">
            <h5 className="font-bold text-blue-900 mb-2">Niveau Jalon</h5>
            <p className="text-sm text-gray-700">Jalons = tâches isjalon=true. Vue Jalons pour suivre les phases et livrables tangibles.</p>
          </div>
          <div className="bg-white p-4 rounded-lg border-2 border-emerald-200">
            <h5 className="font-bold text-emerald-900 mb-2">Niveau Tâche</h5>
            <p className="text-sm text-gray-700">Assignés RACI par tâche (R, A, C, I). Dans le modal <strong>Assignation</strong>, glisser-déposer les contacts dans les colonnes R, A, C, I ; un <strong>guide RACI</strong> pédagogique explique Responsible, Accountable, Consulted, Informed.</p>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <h4 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-bz-blue" />
          Commentaires aux 3 niveaux
        </h4>
        <p className="text-gray-700 leading-relaxed">
          Les commentaires permettent de documenter les décisions, les blocages et les mises à jour au niveau <strong>projet</strong>, <strong>jalon</strong> et <strong>tâche</strong>,
          assurant une traçabilité complète et une collaboration claire entre les acteurs RACI.
        </p>
      </div>
    </div>
  );
}

function ContenuTab() {
  return (
    <div className="space-y-6">
      <div className="bg-linear-to-r from-emerald-50 to-teal-50 p-6 rounded-xl">
        <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Layers className="w-6 h-6 text-emerald-600" />
          Types de projets et trois niveaux
        </h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          On peut créer des projets de <strong>divers types</strong>, répartis sur les <strong>trois niveaux</strong> :
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-lg border-2 border-purple-200">
            <div className="text-lg font-bold text-purple-700 mb-1">Stratégique</div>
            <p className="text-sm text-gray-700">Vision long terme, orientations majeures, 1 à 5 ans.</p>
          </div>
          <div className="bg-white p-4 rounded-lg border-2 border-blue-200">
            <div className="text-lg font-bold text-blue-700 mb-1">Tactique</div>
            <p className="text-sm text-gray-700">Plans annuels, roadmaps, KPI, 3 à 12 mois.</p>
          </div>
          <div className="bg-white p-4 rounded-lg border-2 border-emerald-200">
            <div className="text-lg font-bold text-emerald-700 mb-1">Opérationnel</div>
            <p className="text-sm text-gray-700">Gestion quotidienne, procédures, exécution terrain.</p>
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-4">
          Chaque projet a aussi une <strong>catégorie</strong> (ex. Programmation, Administration, RH, Cloud, Service TI, Cybersécurité, Ventes, CE, CA, Innovation & IA) et des <strong>statuts</strong> : Planifié, En cours, En pause, Terminé, Annulé. Les tâches peuvent être des <strong>jalons</strong> (isjalon) avec livrables tangibles, ou des tactiques avec statut (Planifié, En cours, En pause, Terminé, Potentiel) et priorité (Basse, Moyenne, Haute).
        </p>
      </div>

      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-bz-blue" />
          Vues : Cartes, Liste, Gantt
        </h4>
        <p className="text-gray-700 leading-relaxed mb-4">
          Tout est fait selon les normes <strong>PMBOK</strong>. En haut de la page, un sélecteur permet de basculer entre <strong>Cartes</strong> (ProjectCard : image, priorité, catégorie, type, client, équipe RACI, barre de progression calculée sur les tâches, compteurs tâches/jalons/commentaires, boutons d’action), <strong>Liste</strong> (ProjectsListView) et <strong>Gantt</strong> (ProjectsGanttView). La vue Tâches (TasksModal) propose aussi des vues type Excel, cartes, Gantt et jalons.
        </p>
      </div>

      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="w-6 h-6 text-bz-blue" />
          Rapports : PDF, Excel, KPI, Bonus
        </h4>
        <p className="text-gray-700 leading-relaxed mb-4">
          Depuis une carte projet, le bouton <strong>Rapports</strong> ouvre le ReportModal avec : <strong>Export PDF</strong> (avancement détaillé, tâches, assignés RACI, commentaires), <strong>Export Excel</strong>, <strong>Rapport KPI</strong> (KPIReportModal — indicateurs projet et tâches), et si le projet est en <strong>rémunération variable</strong> (is_remuneration_variable), un <strong>Export PDF Bonus</strong> avec annexe KPI. Les rapports incluent toutes les tâches, commentaires et assignations au moment de l’export.
        </p>
      </div>
    </div>
  );
}

function IATab() {
  return (
    <div className="space-y-6">
      <div className="bg-linear-to-r from-purple-50 to-pink-50 p-6 rounded-xl">
        <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Brain className="w-6 h-6 text-purple-600" />
          Analyse IA : tous les projets et par projet
        </h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          L&apos;analyse IA est disponible à <strong>deux niveaux</strong> : <strong>globale</strong> (tous les projets, portfolio, santé globale, projets critiques)
          et <strong>par projet</strong> (santé du projet, goulots d&apos;étranglement, risques d&apos;échéance, recommandations). Les rapports automatiques par email utilisent aussi l&apos;IA pour générer le contenu.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white p-4 rounded-lg border-2 border-purple-200">
            <h5 className="font-bold text-purple-900 mb-2">Analyse globale</h5>
            <p className="text-sm text-gray-700">Portfolio, projets critiques, progression globale, insights sur l&apos;ensemble des projets filtrés.</p>
          </div>
          <div className="bg-white p-4 rounded-lg border-2 border-blue-200">
            <h5 className="font-bold text-blue-900 mb-2">Analyse par projet</h5>
            <p className="text-sm text-gray-700">Score de santé, tâches en retard, charge des assignés RACI, probabilité de respect des échéances.</p>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar className="w-6 h-6 text-bz-blue" />
          Cédules (Automation) et cron par projet
        </h4>
        <p className="text-gray-700 leading-relaxed mb-4">
          Le bouton <strong>Automation</strong> sur une carte projet ouvre l’AutomationModal : on y gère les <strong>cédules</strong> (schedules) du projet — création via ScheduleForm (nom, récurrence quotidienne/hebdomadaire/mensuelle/ponctuelle, heure, jours du mois ou de la semaine, destinataires RACI R/A/C/I, emails supplémentaires, expéditeur). Chaque cédule envoie des emails automatiques (rapports d’avancement, synthèse IA) selon sa récurrence. Le service <strong>CRON</strong> (cronEmailService) exécute les cédules toutes les minutes (timezone Montréal) ; on peut activer/désactiver une cédule, la tester ou la supprimer. Limite typique : 10 cédules actives par instance.
        </p>
      </div>
    </div>
  );
}

function ProcessusTab() {
  return (
    <div className="space-y-6">
      <div className="bg-linear-to-r from-indigo-50 to-purple-50 p-6 rounded-xl">
        <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Zap className="w-6 h-6 text-indigo-600" />
          Processus de démarrage et gouvernance
        </h3>
        <div className="space-y-4">
          {[
            { step: 1, title: 'Créer le projet dans l\'outil Projets PRIsME', desc: 'Tout nouveau projet BZ doit être ajouté dans l\'outil. C\'est la règle d\'or pour démarrer.' },
            { step: 2, title: 'Rédiger le contenu du projet', desc: 'Le chargé de projet technique définit objectifs, périmètre, jalons et livrables tangibles.' },
            { step: 3, title: 'Attributions RACI (Projet)', desc: 'Assigner A (Eric Hains TI / Francis Marquis Cyber), R (Primes TI / Spécialistes Cyber), C, I.' },
            { step: 4, title: 'Jalons et tâches', desc: 'Créer les jalons (phases) et les tâches. RACI et commentaires au niveau jalon et tâche.' },
            { step: 5, title: 'Cédules et rapports', desc: 'Configurer les cédules email par projet (cron). Générer rapports avancement, Roadmap, KPI selon les besoins.' },
            { step: 6, title: 'Analyse IA', desc: 'Utiliser l\'analyse IA globale et par projet pour piloter les risques et les priorités.' },
          ].map((item) => (
            <div key={item.step} className="flex gap-4 bg-white p-4 rounded-lg border-2 border-indigo-100">
              <div className="w-10 h-10 rounded-full bg-bz-blue text-white font-bold flex items-center justify-center shrink-0">
                {item.step}
              </div>
              <div>
                <h4 className="font-bold text-gray-900">{item.title}</h4>
                <p className="text-sm text-gray-700 mt-1">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-xl">
        <h4 className="text-xl font-bold text-green-900 mb-2 flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          Résultat
        </h4>
        <p className="text-green-800 leading-relaxed">
          Un pilotage de projets <strong>aligné PMBOK</strong>, avec <strong>RACI</strong> et <strong>commentaires</strong> aux trois niveaux,
          <strong> analyse IA</strong> globale et par projet, <strong>cédules personnalisées</strong> par projet, et des <strong>rapports</strong> (avancement, Roadmap, KPI) à la demande ou automatiques.
        </p>
      </div>
    </div>
  );
}
